import { useEffect, useMemo, useState } from 'react';
import { formatDateKey, todayKey } from '../../core/dates';
import { formatPaise, parseAmount } from '../../core/money';
import {
  lentPaise,
  outstandingPaise,
  repaidPaise,
  statusOf,
  totalOutstandingPaise,
  undatedEntries,
  type Receivable,
} from '../../core/receivables';
import type { UserRecord } from '../../data/auth';
import {
  addEntry,
  createReceivable,
  dateEntry,
  seedReceivables,
  setArchived,
  watchReceivables,
} from '../../data/receivables';

interface Props {
  record: UserRecord;
}

/**
 * Receivables (FR-RCV-01 to FR-RCV-08).
 *
 * Every outstanding figure on this screen is derived from the entries beside it
 * (FR-RCV-02). Three of the eight were running arithmetic strings in the workbook, and
 * this is where they become a history you can read rather than a total you have to trust.
 */
export function ReceivablesScreen({ record }: Props) {
  const [receivables, setReceivables] = useState<Receivable[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const [person, setPerson] = useState('');
  const [amount, setAmount] = useState('');
  const [lentOn, setLentOn] = useState(todayKey());

  const [entryAmount, setEntryAmount] = useState('');
  const [entryDate, setEntryDate] = useState(todayKey());
  const [entryIsRepayment, setEntryIsRepayment] = useState(true);

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => watchReceivables(setReceivables), []);

  const visible = useMemo(
    () => (receivables ?? []).filter((r) => showArchived || !r.archived),
    [receivables, showArchived],
  );
  const open = useMemo(
    () => visible.find((r) => r.id === openId) ?? null,
    [visible, openId],
  );

  const undatedCount = useMemo(
    () => (receivables ?? []).reduce((sum, r) => sum + undatedEntries(r).length, 0),
    [receivables],
  );

  async function seed() {
    setError(null);
    setBusy(true);
    try {
      const { count, problems } = await seedReceivables(record.uid);
      setNotice(
        problems.length === 0
          ? `Seeded ${count} receivables, totalling ${formatPaise(9_500_000)}.`
          : `Seeded ${count}, with ${problems.length} to check: ${problems.join(' ')}`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not seed.');
    } finally {
      setBusy(false);
    }
  }

  async function add() {
    setError(null);
    const paise = parseAmount(amount);
    if (paise === null || paise <= 0) {
      setError('Enter an amount.');
      return;
    }
    setBusy(true);
    try {
      await createReceivable(
        { person, amountPaise: paise, lentOn },
        receivables ?? [],
        record.uid,
      );
      setPerson('');
      setAmount('');
      setNotice(`Recorded ${formatPaise(paise)} lent to ${person.trim()}.`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not add that.');
    } finally {
      setBusy(false);
    }
  }

  async function record_entry() {
    if (!open) return;
    setError(null);
    const paise = parseAmount(entryAmount);
    if (paise === null || paise <= 0) {
      setError('Enter an amount.');
      return;
    }
    setBusy(true);
    try {
      await addEntry(
        open,
        {
          // A repayment reduces what is owed, so it is stored negative — the outstanding
          // figure is a plain sum and never a special case.
          amountPaise: entryIsRepayment ? -paise : paise,
          dateKey: entryDate,
          note: null,
        },
        record.uid,
      );
      setEntryAmount('');
      setNotice(
        `${entryIsRepayment ? 'Repayment' : 'Further lending'} of ${formatPaise(paise)} recorded.`,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not record that.');
    } finally {
      setBusy(false);
    }
  }

  async function setDate(receivable: Receivable, index: number, dateKey: string) {
    setError(null);
    try {
      await dateEntry(receivable, index, dateKey, record.uid);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not date that entry.');
    }
  }

  if (receivables === null) return <p className="muted">Loading…</p>;

  if (receivables.length === 0) {
    return (
      <>
        <div className="head-row"><h1>Owed to me</h1></div>
        {error && <p className="error">{error}</p>}
        <div className="placeholder">
          <p>
            No receivables yet. Seeding writes the eight from the <strong>August</strong> sheet —
            including the three whose balances were running arithmetic in the workbook, which
            become an entry each.
          </p>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void seed()}>
            {busy ? 'Seeding…' : 'Seed from the workbook'}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="head-row">
        <h1>Owed to me</h1>
        <label className="inline">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      <div className="figures">
        <div className="figure emphasis">
          <div className="figure-label">Total outstanding</div>
          <div className="figure-value">{formatPaise(totalOutstandingPaise(visible))}</div>
        </div>
        <div className="figure">
          <div className="figure-label">People</div>
          <div className="figure-value">
            {visible.filter((r) => statusOf(r) === 'outstanding').length}
          </div>
          <div className="muted tiny">{visible.filter((r) => statusOf(r) === 'settled').length} settled</div>
        </div>
        {undatedCount > 0 && (
          <div className="figure">
            <div className="figure-label">Entries needing a date</div>
            <div className="figure-value">{undatedCount}</div>
            {/* The workbook records the arithmetic but not the calendar, so these arrive
                undated rather than with invented dates (FR-IMP-11). */}
            <div className="muted tiny">from the workbook, which recorded no dates</div>
          </div>
        )}
      </div>

      <div className="table-scroll">
        <table className="table reclassify-table">
          <thead>
            <tr>
              <th>Person</th>
              <th className="num">Lent</th>
              <th className="num">Repaid</th>
              <th className="num">Outstanding</th>
              <th>Entries</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((receivable) => (
              <tr
                key={receivable.id}
                className={openId === receivable.id ? 'selected' : undefined}
                onClick={() => setOpenId(openId === receivable.id ? null : receivable.id)}
              >
                <td>
                  {receivable.person}
                  {receivable.note && <div className="muted tiny">{receivable.note}</div>}
                  {receivable.archived && <span className="badge">archived</span>}
                </td>
                <td className="num">{formatPaise(lentPaise(receivable), { symbol: false })}</td>
                <td className="num">{formatPaise(repaidPaise(receivable), { symbol: false })}</td>
                <td className="num">
                  <strong>{formatPaise(outstandingPaise(receivable), { symbol: false })}</strong>
                </td>
                <td>
                  {receivable.entries.length}
                  {receivable.source === 'reconstructed' && (
                    <span className="badge" title="Rebuilt from the workbook's running expression">
                      reconstructed
                    </span>
                  )}
                </td>
                <td className="actions">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      void setArchived(receivable, !receivable.archived, record.uid);
                    }}
                  >
                    {receivable.archived ? 'Restore' : 'Archive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <section className="panel">
          <h2>{open.person}</h2>
          <p className="muted tiny">
            Outstanding is the sum of these entries and is never stored separately
            (FR-RCV-02).
          </p>

          <table className="table compact">
            <tbody>
              {open.entries.map((entry, index) => (
                <tr key={index}>
                  <td>
                    {entry.dateKey ? (
                      formatDateKey(entry.dateKey, 'dd MMM yyyy')
                    ) : (
                      <input
                        type="date"
                        aria-label={`Date entry ${index + 1}`}
                        onChange={(e) => e.target.value && void setDate(open, index, e.target.value)}
                      />
                    )}
                    {entry.note && <div className="muted tiny">{entry.note}</div>}
                  </td>
                  <td className="num">
                    <span className={entry.amountPaise < 0 ? 'ok' : undefined}>
                      {entry.amountPaise < 0 ? 'repaid ' : 'lent '}
                      {formatPaise(Math.abs(entry.amountPaise), { symbol: false })}
                    </span>
                  </td>
                </tr>
              ))}
              <tr className="total-row">
                <td><strong>Outstanding</strong></td>
                <td className="num"><strong>{formatPaise(outstandingPaise(open))}</strong></td>
              </tr>
            </tbody>
          </table>

          <div className="row">
            <select
              value={entryIsRepayment ? 'repaid' : 'lent'}
              onChange={(e) => setEntryIsRepayment(e.target.value === 'repaid')}
              aria-label="Entry type"
            >
              <option value="repaid">Repaid to me</option>
              <option value="lent">Lent further</option>
            </select>
            <input
              type="text"
              inputMode="decimal"
              value={entryAmount}
              onChange={(e) => setEntryAmount(e.target.value)}
              placeholder="Amount"
              aria-label="Entry amount"
            />
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              aria-label="Entry date"
            />
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void record_entry()}>
              Record
            </button>
          </div>
        </section>
      )}

      <section className="panel">
        <h2>Lend to someone new</h2>
        <div className="row">
          <input
            type="text"
            value={person}
            onChange={(e) => setPerson(e.target.value)}
            placeholder="Who"
            aria-label="Person"
          />
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount"
            aria-label="Amount"
          />
          <input
            type="date"
            value={lentOn}
            onChange={(e) => setLentOn(e.target.value)}
            aria-label="Date lent"
          />
          <button type="button" className="btn btn-primary" disabled={busy || !person.trim()} onClick={() => void add()}>
            Add
          </button>
        </div>
      </section>
    </>
  );
}
